/**
 * LocationPicker
 * ---------------
 * Reusable geolocation input for booking flows. Combines:
 *   - Google Places autocomplete (country: CM)
 *   - "Ma position actuelle" GPS button (reverse-geocoded to a friendly address)
 *   - Read-only display of the picked address once selected
 *
 * Emits a full `{ address, lat, lng }` object via `onChange` so backend can
 * persist coordinates and the driver can navigate to a real GPS pin.
 *
 * Requires the parent tree to be wrapped inside `<APIProvider>` from
 * `@vis.gl/react-google-maps` so `useMapsLibrary("places")` is available.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { MapPin, Loader2, LocateFixed, XCircle, Home, Briefcase, Plane, Star, Plus, Trash2 } from "lucide-react";
import api from "../lib/api";

const ICON_MAP = { home: Home, work: Briefcase, airport: Plane, custom: Star };
const ICON_LABELS = { home: "Maison", work: "Bureau", airport: "Aéroport", custom: "Favori" };

export default function LocationPicker({
  value,
  onChange,
  placeholder = "Rechercher un lieu…",
  label,
  testId,
  bias,               // { lat, lng } used to bias search results near this centre
  disabled = false,
  compact = false,    // reduces padding
  pinColor = "#0A2540",
  showGpsButton = true,
  showFavorites = true,  // renders quick-tap favorite chips + save button
  savePresetIcon = null, // when set (e.g. "home"), the save button auto-uses this icon
  autoGpsOnMount = false, // fires "Ma position actuelle" once at mount if value is empty
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const placesLib = useMapsLibrary("places");
  const geocodingLib = useMapsLibrary("geocoding");
  const [text, setText] = useState(value?.address || "");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favLoading, setFavLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveIcon, setSaveIcon] = useState(savePresetIcon || "custom");
  const [saveLabel, setSaveLabel] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => { setText(value?.address || ""); }, [value?.address]);

  // Bind autocomplete once the Places lib is ready
  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    const opts = {
      fields: ["formatted_address", "geometry", "name", "place_id"],
      componentRestrictions: { country: "cm" },
    };
    if (bias?.lat && bias?.lng) {
      opts.bounds = new window.google.maps.LatLngBounds(
        { lat: bias.lat - 0.15, lng: bias.lng - 0.15 },
        { lat: bias.lat + 0.15, lng: bias.lng + 0.15 }
      );
      opts.strictBounds = false;
    }
    autocompleteRef.current = new placesLib.Autocomplete(inputRef.current, opts);
    const listener = autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current?.getPlace();
      if (!place?.geometry?.location) return;
      const picked = {
        address: place.formatted_address || place.name || text,
        name: place.name || "",
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        place_id: place.place_id || null,
      };
      setText(picked.address);
      onChange?.(picked);
    });
    return () => { if (listener) window.google.maps.event.removeListener(listener); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placesLib, bias?.lat, bias?.lng]);

  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!geocodingLib) return null;
    try {
      const geocoder = new geocodingLib.Geocoder();
      const res = await geocoder.geocode({ location: { lat, lng } });
      const first = res?.results?.[0];
      if (first) return { address: first.formatted_address, name: first.formatted_address, place_id: first.place_id };
    } catch (_e) { /* fall through */ }
    return null;
  }, [geocodingLib]);

  const requestCurrentPosition = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGpsError("La géolocalisation n'est pas supportée sur cet appareil.");
      return;
    }
    setGpsError(null);
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const rev = await reverseGeocode(lat, lng);
        const picked = {
          address: rev?.address || `GPS ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          name: rev?.name || "Ma position actuelle",
          lat, lng,
          place_id: rev?.place_id || null,
        };
        setText(picked.address);
        onChange?.(picked);
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        const msg = err.code === 1
          ? "Autorisation GPS refusée. Activez la localisation puis réessayez."
          : err.code === 3
          ? "Position introuvable. Vérifiez votre signal GPS."
          : "Impossible de récupérer votre position.";
        setGpsError(msg);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  }, [reverseGeocode, onChange]);

  const clear = useCallback(() => {
    setText("");
    onChange?.(null);
  }, [onChange]);

  // Load favorites once (component-scoped cache).
  const refreshFavorites = useCallback(async () => {
    if (!showFavorites) return;
    setFavLoading(true);
    try {
      const { data } = await api.get("/me/places");
      setFavorites(Array.isArray(data) ? data : []);
    } catch (_e) { /* silent — user might be logged out */ }
    finally { setFavLoading(false); }
  }, [showFavorites]);
  useEffect(() => { refreshFavorites(); }, [refreshFavorites]);

  // Auto-prefill pickup with the passenger's current GPS on first mount so
  // recurring users don't have to type anything. Runs at most once, and only
  // when the caller opts in AND the picker is empty AND reverse-geocoding is
  // ready (otherwise the address label would be a raw lat/lng string).
  const autoTriedRef = useRef(false);
  useEffect(() => {
    if (!autoGpsOnMount || autoTriedRef.current) return;
    if (value?.lat || text) return;                 // already filled
    if (!geocodingLib) return;                       // wait for reverse-geocoder
    if (!("geolocation" in navigator)) return;
    autoTriedRef.current = true;
    requestCurrentPosition();
  }, [autoGpsOnMount, geocodingLib, value?.lat, text, requestCurrentPosition]);

  const pickFavorite = useCallback((fp) => {
    const picked = { address: fp.address, lat: fp.lat, lng: fp.lng, name: fp.label };
    setText(picked.address);
    onChange?.(picked);
  }, [onChange]);

  const openSave = useCallback(() => {
    if (!value?.lat || !value?.lng) return;
    setSaveIcon(savePresetIcon || "custom");
    setSaveLabel(value.name || value.address?.split(",")[0] || "");
    setSaveError("");
    setSaveOpen(true);
  }, [value, savePresetIcon]);

  const saveFavorite = useCallback(async () => {
    if (!value?.lat || !value?.lng) return;
    if (!saveLabel.trim()) { setSaveError("Nom requis"); return; }
    try {
      await api.post("/me/places", {
        label: saveLabel.trim(),
        address: value.address,
        lat: value.lat, lng: value.lng,
        icon: saveIcon,
      });
      setSaveOpen(false);
      refreshFavorites();
    } catch (e) { setSaveError(e?.response?.data?.detail || "Erreur d'enregistrement"); }
  }, [value, saveLabel, saveIcon, refreshFavorites]);

  const deleteFavorite = useCallback(async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/me/places/${id}`);
      refreshFavorites();
    } catch (_e) { /* silent */ }
  }, [refreshFavorites]);

  const padY = compact ? "py-2" : "py-2.5";
  const hasCoords = Boolean(value?.lat && value?.lng);
  const alreadyFavorited = hasCoords && favorites.some(f => Math.abs(f.lat - value.lat) < 1e-5 && Math.abs(f.lng - value.lng) < 1e-5);

  return (
    <div className="w-full space-y-1" data-testid={testId ? `${testId}-picker` : undefined}>
      {label && <label className="text-[10px] text-[#64748B] font-semibold uppercase tracking-wide">{label}</label>}
      {showFavorites && favorites.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pb-1" data-testid={testId ? `${testId}-favorites` : undefined}>
          {favorites.map((fp) => {
            const Icon = ICON_MAP[fp.icon] || Star;
            return (
              <button
                key={fp.id}
                type="button"
                onClick={() => pickFavorite(fp)}
                className="group inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FACC15]/15 border border-[#FACC15]/40 text-[11px] font-semibold text-[#0A2540] hover:bg-[#FACC15]/30 transition"
                title={fp.address}
                data-testid={testId ? `${testId}-fav-${fp.icon}-${fp.id}` : undefined}
              >
                <Icon className="w-3 h-3" />
                <span className="max-w-[110px] truncate">{fp.label}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => deleteFavorite(fp.id, e)}
                  className="opacity-0 group-hover:opacity-100 transition text-[#EF4444] cursor-pointer"
                  aria-label={`Supprimer ${fp.label}`}
                  data-testid={testId ? `${testId}-fav-delete-${fp.id}` : undefined}
                >
                  <Trash2 className="w-3 h-3" />
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div className={`flex items-center gap-2 rounded-xl border border-[#CBD5E1] bg-white px-3 ${padY} focus-within:border-[#0A2540] transition`}>
        <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: pinColor }} />
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#0A2540] placeholder:text-[#94A3B8] disabled:opacity-60"
          data-testid={testId}
          autoComplete="off"
        />
        {text && !disabled && (
          <button type="button" onClick={clear} className="text-[#94A3B8] hover:text-[#0A2540] transition flex-shrink-0" aria-label="Effacer" data-testid={testId ? `${testId}-clear` : undefined}>
            <XCircle className="w-3.5 h-3.5" />
          </button>
        )}
        {showGpsButton && (
          <button
            type="button"
            onClick={requestCurrentPosition}
            disabled={disabled || gpsLoading}
            className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 disabled:opacity-60 transition"
            data-testid={testId ? `${testId}-gps-btn` : undefined}
            title="Utiliser ma position actuelle"
          >
            {gpsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3" />}
            <span className="hidden sm:inline">GPS</span>
          </button>
        )}
      </div>
      {gpsError && (
        <p className="text-[10px] text-[#EF4444]" data-testid={testId ? `${testId}-gps-error` : undefined}>{gpsError}</p>
      )}
      {hasCoords && (
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <p className="text-[10px] text-[#10B981] font-medium" data-testid={testId ? `${testId}-coords` : undefined}>
            📍 Coordonnées GPS enregistrées ({value.lat.toFixed(5)}, {value.lng.toFixed(5)})
          </p>
          {showFavorites && !alreadyFavorited && (
            <button
              type="button"
              onClick={openSave}
              className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0A2540] hover:text-[#FACC15] transition"
              data-testid={testId ? `${testId}-save-fav-btn` : undefined}
            >
              <Plus className="w-3 h-3" /> Enregistrer
            </button>
          )}
        </div>
      )}
      {saveOpen && (
        <div className="mt-1 p-2 rounded-lg border border-[#FACC15] bg-[#FACC15]/10 space-y-1.5" data-testid={testId ? `${testId}-save-form` : undefined}>
          <p className="text-[10px] font-bold text-[#0A2540] uppercase tracking-wide">Enregistrer comme favori</p>
          <div className="flex flex-wrap gap-1">
            {["home", "work", "airport", "custom"].map((ic) => {
              const Ic = ICON_MAP[ic];
              return (
                <button
                  key={ic}
                  type="button"
                  onClick={() => { setSaveIcon(ic); if (!saveLabel || ["Maison","Bureau","Aéroport","Favori"].includes(saveLabel)) setSaveLabel(ICON_LABELS[ic]); }}
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full transition ${saveIcon === ic ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540] border border-[#CBD5E1] hover:border-[#0A2540]"}`}
                  data-testid={testId ? `${testId}-save-icon-${ic}` : undefined}
                >
                  <Ic className="w-3 h-3" /> {ICON_LABELS[ic]}
                </button>
              );
            })}
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={saveLabel}
              onChange={(e) => setSaveLabel(e.target.value)}
              placeholder="Nom du favori"
              className="flex-1 rounded-md border border-[#CBD5E1] px-2 py-1 text-xs outline-none focus:border-[#0A2540]"
              maxLength={60}
              data-testid={testId ? `${testId}-save-label-input` : undefined}
            />
            <button
              type="button"
              onClick={saveFavorite}
              className="px-3 py-1 rounded-md bg-[#0A2540] text-white text-[11px] font-bold hover:bg-[#0A2540]/90"
              data-testid={testId ? `${testId}-save-submit` : undefined}
            >
              Enregistrer
            </button>
            <button
              type="button"
              onClick={() => setSaveOpen(false)}
              className="px-2 py-1 text-[11px] text-[#64748B]"
              data-testid={testId ? `${testId}-save-cancel` : undefined}
            >
              Annuler
            </button>
          </div>
          {saveError && <p className="text-[10px] text-[#EF4444]">{saveError}</p>}
        </div>
      )}
    </div>
  );
}
